import { IProjectState, TGetRemoveUserRolesFromProjectPayload, TProjectUser, TProjectSetting } from './types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TProject } from './types'
import {
  getPostsForApprovalThunk,
  getProjectUsersByRoleThunk,
  updateProjectThunk,
  removeUserRolesFromProjectThunk,
  softDeleteProjectThunk,
  updateUserSettingFromProjectThunk,
  getApprovedPostsThunk,
  getPendingApprovalsCountThunk,
  checkUserHavePostToApproveThunk
} from './thunks'
import { POST_SOCIAL_STATUS, SOCIAL_NETWORK } from '../../models/post'
import { approveCurrentRoundThunk, unApproveCurrentRoundThunk, requireApproverRevisionThunk } from '../managePost/thunks'
import { TAddProjectSocialPayload, TUpdateProjectSocialPayload } from '../agency/types'
import Roles from '../../models/roles'

const initialState: IProjectState = {
  currentProject: {
    id: 0,
    name: '',
    agency_id: 0,
    owner_id: 0,
    logo_image_link: null,
    timezone: '',
    role: Roles.CONTENT_CREATOR,
    socials: [],
    postsToApproval: [],
    approvedPosts: [],
    projectUsers: [],
    setting:[]
  },
  availableSocialNetworks: [],
  loaders: {
    project: {
      update: false,
      delete: false
    },
    users: {
      delete: false
    },
    approval: {
      loading: false
    }
  },
  pendingApprovalsCount: [],
  checkResult: '',
}

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    chooseProjectAction: (state, action: PayloadAction<TProject>) => {
      state.currentProject = {
        ...state.currentProject,
        ...action.payload,
        projectUsers: [],
        postsToApproval: [],
        approvedPosts: []
      }
      state.availableSocialNetworks = action.payload.socials.map<SOCIAL_NETWORK>(
        ({ social_network }) => social_network
      )
    },
    resetPostsForApprovavl: (state) => {
      state.currentProject.postsToApproval = []
      state.currentProject.approvedPosts = []
    },
    addProjectSocialToCurrentProject: (state, action: PayloadAction<TAddProjectSocialPayload>) => {
      const { projectSocial } = action.payload
      state.currentProject.socials.push(projectSocial)
      state.availableSocialNetworks = state.currentProject.socials.map<SOCIAL_NETWORK>(
        ({ social_network }) => social_network
      )
    },
    loadSocialsOnSchedule: (state, action: PayloadAction<TUpdateProjectSocialPayload[]>) => {
      state.currentProject.socials = action.payload
      state.availableSocialNetworks = state.currentProject.socials.map<SOCIAL_NETWORK>(
        ({ social_network }) => social_network
      )
    },
    removeProjectSocialAction: (state, action: PayloadAction<SOCIAL_NETWORK>) => {
      state.currentProject.socials = state.currentProject.socials.filter(
        ({ social_network }) => social_network !== action.payload
      )
    },
    resetProjectStateAction: () => initialState
  },
  extraReducers: (builder) => {
    // Get posts for approval
    builder.addCase(getPostsForApprovalThunk.pending, (state, action) => {
      state.loaders.approval.loading = true
    })
    builder.addCase(getPostsForApprovalThunk.fulfilled, (state, action) => {
      // todo remove filter late. need for comparable with lates backend version
      state.loaders.approval.loading = false
      state.currentProject.postsToApproval = action.payload.data.filter(
        (post: any) => !!post.latest_schedule?.date && !!post.status && !!post.id
      )
    })
    builder.addCase(getPostsForApprovalThunk.rejected, (state, action) => {
      state.loaders.approval.loading = false
    })

    // Get approved posts
    builder.addCase(getApprovedPostsThunk.pending, (state, action) => {
      state.loaders.approval.loading = true
    })
    builder.addCase(getApprovedPostsThunk.fulfilled, (state, action) => {
      state.loaders.approval.loading = false
      state.currentProject.approvedPosts = action.payload.data.filter(
        (post: any) => !!post.latest_schedule?.date && !!post.status && !!post.id
      )
    })
    builder.addCase(getApprovedPostsThunk.rejected, (state, action) => {
      state.loaders.approval.loading = false
    })
    
    // Approve current round by approvers
    builder.addCase(approveCurrentRoundThunk.fulfilled, (state, action) => {
      const { post_socials: updatedPostSocials, post_id: postId } = action.payload.data
      state.currentProject.postsToApproval = state.currentProject.postsToApproval.map(
        (postToApprove) => {
          return postToApprove.id !== postId
            ? postToApprove
            : {
                ...postToApprove,
                post_socials: postToApprove.post_socials.map((postSocial) => {
                  const updatedSocial = updatedPostSocials.find(
                    (updatedSocial: any) => updatedSocial.id === postSocial.id
                  )

                  return {
                    ...postSocial,
                    status: updatedSocial ? updatedSocial.status : postSocial.status,
                    is_approved: updatedSocial ? updatedSocial.is_approved : false
                  }
                })
              }
        }
      )
    })

    // Unapprove udate post
    builder.addCase(unApproveCurrentRoundThunk.fulfilled, (state, action) => {
      const { post_socials: updatedPostSocials, post_id: postId } = action.payload.data
      state.currentProject.approvedPosts = state.currentProject.approvedPosts.map(
        (approvedPost) => {
          return approvedPost.id !== postId
            ? approvedPost
            : {
                ...approvedPost,
                post_socials: approvedPost.post_socials.map((postSocial) => {
                  const updatedSocial = updatedPostSocials.find( (updatedSocial: any) => updatedSocial.id === postSocial.id)

                  return {
                    ...postSocial,
                    status: updatedSocial ? updatedSocial.status : postSocial.status,
                    is_unapproved: updatedSocial ? updatedSocial.is_unapproved : false
                  }
                })
              }
      })
    }) 

    // Request revision update post
    builder.addCase(requireApproverRevisionThunk.fulfilled, (state, action) => {
      const { post_social_id: postSocialId, post_id: postId, isMasterContent } = action.meta.arg
      state.currentProject.postsToApproval = state.currentProject.postsToApproval.map(
        (postToApprove) => {
          return postToApprove.id !== postId
            ? postToApprove
            : 
            {
                ...postToApprove,
                post_socials: postToApprove.post_socials.map((postSocial) => {
                  return {
                    ...postSocial,
                    status:
                      postSocial.id === postSocialId || isMasterContent
                        ? POST_SOCIAL_STATUS.STATUS_REQUIRES_REVISION
                        : postSocial.status,
                      is_approved:false  
                  }
                })
            }
        }
      )
    })

    // Get progect users
    builder.addCase(getProjectUsersByRoleThunk.pending, (state, action) => {})
    builder.addCase(
      getProjectUsersByRoleThunk.fulfilled,
      (state, action: PayloadAction<TProjectUser[]>) => {
        state.currentProject.projectUsers = action.payload
      }
    )
    builder.addCase(getProjectUsersByRoleThunk.rejected, (state, action) => {})
    // Update the project
    builder.addCase(updateProjectThunk.pending, (state, action) => {
      state.loaders.project.update = true
    })
    builder.addCase(updateProjectThunk.fulfilled, (state, action: PayloadAction<TProject>) => {
      state.loaders.project.update = false
      state.currentProject = {
        ...state.currentProject,
        ...action.payload
      }
    })
    builder.addCase(updateProjectThunk.rejected, (state, action) => {
      state.loaders.project.update = false
    })
    // Delete the project
    builder.addCase(softDeleteProjectThunk.pending, (state, action) => {
      state.loaders.project.delete = true
    })
    builder.addCase(softDeleteProjectThunk.fulfilled, (state, action) => {
      state.loaders.project.delete = false
    })
    builder.addCase(softDeleteProjectThunk.rejected, (state, action) => {
      state.loaders.project.delete = false
    })
    // Remove user roles from project
    builder.addCase(removeUserRolesFromProjectThunk.pending, (state, action) => {
      state.loaders.users.delete = true
    })
    builder.addCase(removeUserRolesFromProjectThunk.fulfilled, (state, action) => {
      state.loaders.users.delete = false
      const userId = +action.meta.arg.user_id
      state.currentProject.projectUsers = state.currentProject.projectUsers.filter(
        (user) => userId !== user.id
      )
    })
    builder.addCase(removeUserRolesFromProjectThunk.rejected, (state, action) => {
      state.loaders.users.delete = false
    })
    
    //update user settings
    builder.addCase(updateUserSettingFromProjectThunk.pending, (state, action) => {
      state.loaders.project.update = true
    })
    builder.addCase(updateUserSettingFromProjectThunk.fulfilled, (state, action) => {
      state.loaders.project.update = false
    })
    builder.addCase(updateUserSettingFromProjectThunk.rejected, (state, action) => {
      state.loaders.project.update = false
    })

    //to get the approvers count
    builder.addCase(getPendingApprovalsCountThunk.pending, (state, action) => {})
    builder.addCase(getPendingApprovalsCountThunk.fulfilled, (state, action) => {
        state.pendingApprovalsCount = action.payload
    })
    builder.addCase(getPendingApprovalsCountThunk.rejected, (state, action) => {})

    //to get the approvers count
    builder.addCase(checkUserHavePostToApproveThunk.pending, (state, action) => {})
    builder.addCase(checkUserHavePostToApproveThunk.fulfilled, (state, action) => {
        state.checkResult = action.payload
    })
    builder.addCase(checkUserHavePostToApproveThunk.rejected, (state, action) => {})
  }
})

export const {
  chooseProjectAction,
  removeProjectSocialAction,
  addProjectSocialToCurrentProject,
  loadSocialsOnSchedule,
  resetProjectStateAction,
  resetPostsForApprovavl
} = projectSlice.actions
export default projectSlice.reducer
